import { useEffect, useRef, useState } from "react";
import { AuthContextProps, hasAuthParams } from "react-oidc-context";

import { logError } from "@/lib/logError";
import { reportSilentFailure } from "@/lib/reportSilentFailure";
import { clearSplashAutoRecoverAttempts } from "@/lib/splashAutoRecover";

import { ParkedSignIn } from "./ParkedSignIn";
import { resolveAuthAction } from "./resolveAuthAction";
import {
  clearSigninRedirectAttempts,
  getSigninRedirectAttemptStats,
  isSigninRedirectExhausted,
  recordSigninRedirectAttempt,
} from "./signinRedirectAttempts";

type AuthControllerProps = Partial<AuthContextProps> &
  React.PropsWithChildren<{
    isAutoLogin?: boolean;
    isPublicRoute?: boolean;
    onAuthenticatedPublicRoute?: () => void;
    /**
     * Skip the redirect/renewal state machine and render children
     * immediately. Set by the mock auth protection (E2E test build and
     * runtime mock mode), whose session is fake and always authenticated —
     * redirecting a public route to the dashboard or bouncing to Keycloak
     * would break those flows. Never set by the real AuthProtection.
     */
    bypassGuards?: boolean;
  }>;

const AuthController = ({
  error,
  isLoading,
  isAuthenticated,
  activeNavigator,
  user,
  signinRedirect,
  signinSilent,
  isAutoLogin,
  isPublicRoute,
  onAuthenticatedPublicRoute,
  bypassGuards = false,
  children,
}: AuthControllerProps) => {
  const [isReady, setIsReady] = useState(false);

  // True while the automatic-redirect budget is spent, so the render gate can
  // show the ParkedSignIn recovery screen instead of nothing.
  const [isParked, setIsParked] = useState(false);

  // Latches once a signin redirect has been initiated for this auth-resolution
  // cycle. Without it, the effect re-fired signinRedirect every time the OIDC
  // navigator closed while the user was still unauthenticated (e.g. iOS WebKit
  // losing the OIDC state to storage partitioning on the Keycloak callback) —
  // a redirect loop that overflowed the call stack (RangeError: Maximum call
  // stack size exceeded). See Sentry CUSTOMER-UI-3T and
  // react-oidc-context's own `useAutoSignin` / `withAuthenticationRequired`,
  // which apply the same hasTriedSignin / activeNavigator / hasAuthParams guards.
  const hasTriedSignin = useRef(false);

  // Latches once the recovery silent renewal for an expired-but-renewable
  // stored user has been attempted this auth-resolution cycle, so a failed
  // attempt falls through to the redirect instead of looping renewals.
  const hasTriedSilentRenew = useRef(false);

  // One Sentry event per mount when the redirect budget parks us on the
  // sign-in recovery screen — the effect re-runs on auth-state changes and
  // must not spam.
  const hasReportedExhaustion = useRef(false);

  useEffect(() => {
    // With a mock session the render gate (below) already returns children
    // immediately. Skip redirect logic to prevent public routes being
    // redirected to the dashboard.
    if (bypassGuards) return;

    // Becoming authenticated clears the latches (so a later token expiry on a
    // long-lived session can renew/redirect again) and the cross-page-load
    // redirect budget (the loop guard only targets failing cycles).
    if (isAuthenticated) {
      hasTriedSignin.current = false;
      hasTriedSilentRenew.current = false;
      clearSigninRedirectAttempts();
      // A successful boot also retires the splash auto-recovery budget, so a
      // later genuine wedge in this same session can self-heal again rather
      // than being blocked by a stale one-shot attempt from earlier.
      clearSplashAutoRecoverAttempts();
    }

    const authParamsPresent = hasAuthParams();
    const action = resolveAuthAction({
      isLoading: isLoading ?? false,
      activeNavigator,
      isAuthenticated: isAuthenticated ?? false,
      isPublicRoute: isPublicRoute ?? false,
      isAutoLogin: isAutoLogin ?? false,
      error,
      // While returning FROM Keycloak (?code|error + state in the URL), let
      // AuthProvider process the callback instead of re-redirecting.
      hasAuthParams: authParamsPresent,
      hasTriedSignin: hasTriedSignin.current,
      signinRedirectExhausted: isSigninRedirectExhausted(),
      // `expired` can be undefined (no expires_at) — treat unknowable as
      // expired so the wait-on-renewal-error branch can't wait forever.
      tokenExpired: user?.expired !== false,
      hasRenewableUser: Boolean(user?.refresh_token && signinSilent),
      hasTriedSilentRenew: hasTriedSilentRenew.current,
    });

    const parkedNow =
      action.type === "wait" && action.reason === "redirect-budget-exhausted";
    setIsParked((prev) => (prev === parkedNow ? prev : parkedNow));

    switch (action.type) {
      case "wait":
        // Transient renewal failure with validity left on the access token:
        // keep the app rendered and let the re-attempt layers (mid-window
        // re-try, refocus/online listeners, last-chance renew at expiry)
        // recover it. Breadcrumb only — the silentRenewError handler in
        // AuthProvider owns the alerting decision.
        if (action.reason === "renewal-error-token-still-valid") {
          console.warn(
            "[AuthController] Silent renewal failing but access token still valid — staying on page"
          );
          return;
        }
        // The deliberate parked state: automatic signinRedirects kept
        // failing (recurring callback failure / redirect loop) and the
        // cross-page-load budget is spent. The user sees the ParkedSignIn
        // recovery screen (rendered by the gate below); nothing throws here,
        // so this report is the team's only notification.
        if (
          action.reason === "redirect-budget-exhausted" &&
          !hasReportedExhaustion.current
        ) {
          hasReportedExhaustion.current = true;
          const stats = getSigninRedirectAttemptStats();
          reportSilentFailure({
            message:
              "[Auth] Signin-redirect budget exhausted — user parked on the sign-in recovery screen",
            fingerprint: "auth-redirect-budget-exhausted",
            tags: { "auth.redirectLoop": "true" },
            extra: {
              hasOidcError: Boolean(error),
              hasAuthCallbackParams: authParamsPresent,
              // Raw pace evidence: a machine loop packs attempts seconds
              // apart; a person bouncing pages spreads them out. Reported,
              // not thresholded — cold starts blur the line (CUSTOMER-UI-6V).
              attemptsInWindow: stats.attemptsInWindow,
              minGapMs: stats.minGapMs ?? -1, // never null once exhausted (≥3 attempts); -1 only satisfies the extra's number type
            },
          });
        }
        return;
      case "ready":
        setIsReady(true);
        return;
      case "authenticated-public":
        onAuthenticatedPublicRoute?.();
        return;
      case "silent-renew": {
        hasTriedSilentRenew.current = true;
        // Recover an expired-but-renewable session IN PLACE: a refresh-token
        // grant navigates nowhere, so success (USER_LOADED) preserves the
        // user's in-progress work; failure dispatches an ERROR that re-enters
        // this effect (tokenExpired now true) and falls through to the
        // sign-in redirect below.
        console.warn(
          "[AuthController] Token expired — attempting silent recovery before redirecting to login"
        );
        void signinSilent?.().catch(() => {
          // react-oidc-context's ERROR dispatch drives the redirect path;
          // catching here only prevents an unhandled rejection.
        });
        return;
      }
      case "signin": {
        hasTriedSignin.current = true;
        // Count toward the cross-page-load budget BEFORE navigating, so a
        // failure loop (app↔Keycloak across page loads) self-limits.
        recordSigninRedirectAttempt();
        const pathname =
          typeof window !== "undefined" ? window.location.pathname : "unknown";
        if (action.reason === "oidc-error") {
          logError(
            "[AuthController] OIDC error on protected route — redirecting to login",
            error,
            { pathname }
          );
        } else {
          logError(
            "[AuthController] Token expired on protected route — redirecting to login",
            undefined,
            { pathname }
          );
        }
        // A FAILED Keycloak callback (?error + state) never reaches
        // onSigninCallback, so its params are still in the URL. Strip them
        // before redirecting so a back-navigation or a failed redirect can't
        // re-enter the failed-callback state (frozen splash screen).
        if (authParamsPresent && typeof window !== "undefined") {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
        // Always pass an explicit redirect_uri (current path, query stripped) so
        // the post-login return target is deterministic across whitelabel
        // domains, matching the configured UserManager redirect_uri.
        signinRedirect?.({
          redirect_uri:
            typeof window !== "undefined"
              ? window.location.href.split("?")[0]
              : undefined,
        });
        return;
      }
    }
    // Note: We intentionally omit onAuthenticatedPublicRoute, signinRedirect,
    // signinSilent and user from deps because we only want this effect to run
    // when auth state changes, not when callbacks change (user's token state
    // is read fresh whenever a state change re-runs the effect; expiry itself
    // re-enters via the ERROR / navigator dispatches, not via user identity)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isLoading,
    isPublicRoute,
    isAutoLogin,
    error,
    activeNavigator,
    bypassGuards,
  ]);

  if (bypassGuards) return children;

  if (isParked) return <ParkedSignIn signinRedirect={signinRedirect} />;

  if (isReady && (isAuthenticated || isPublicRoute)) {
    return children;
  }
};

export default AuthController;
